import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@enamelai/design-system'
import { Icon as ChevronDownIcon } from '@enamelai/design-system/icons/Arrow_down_chevron'
import './SettingsDropdownButton.css'

export type SettingsDropdownItem = {
  id: string
  label: string
  onSelect: () => void
}

type SettingsDropdownButtonProps = {
  label: string
  items: SettingsDropdownItem[]
}

/** Primary header button with a chevron that opens a short action menu. */
const SettingsDropdownButton: React.FC<SettingsDropdownButtonProps> = ({
  label,
  items,
}) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div className="settings-dropdown" ref={rootRef}>
      <Button
        className="settings-ds-button"
        variant="primary"
        size="md"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        rightIcon={<ChevronDownIcon width={16} height={16} aria-hidden />}
        onClick={() => setOpen((prev) => !prev)}
      >
        {label}
      </Button>
      {open && (
        <div className="settings-dropdown__list" role="menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="settings-dropdown__item"
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default SettingsDropdownButton
